import React from 'react';
import type { Editor } from 'ckeditor5';
import { observer } from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import DefaultButton from '../../../../../../global/default-button/default-button';
import { FormField } from '../../../../../../global/form/form-field/form-field';
import { OEToggle } from '../../../../../../global/oe-toggle/oe-toggle';
import { CommonModal } from '../../../../../../global/modal/common/common-modal';
import { RichTextEditor } from '../../../../../../global/rich-text-editor';
import { ADD_TEXT, CANCEL_TEXT } from '../../../../../../../constants/common-labels.constants';
import { HubCustomFieldType } from '../../../../../../../api/hub-registration-configuration.dao';
import {
    HUB_REGISTRATION_FIELD_DISPLAYED_TOGGLE_TEXT,
    HUB_REGISTRATION_FIELD_REQUIRED_TOGGLE_TEXT,
    HUB_CUSTOM_FIELD_TYPE_TEXT_TEXT,
    HUB_CUSTOM_FIELD_TYPE_DROPDOWN_TEXT,
    HUB_CUSTOM_FIELD_TYPE_TEXTAREA_TEXT,
    HUB_CUSTOM_FIELD_TYPE_CHECKBOX_TEXT,
} from '../../../../../event-hubs.text';
import { HubRegistrationSettingsVM } from './hub-registration-settings.viewmodel';
import './hub-custom-field-modal.scss';

type HubCustomFieldModalProps = {
    vm: HubRegistrationSettingsVM;
};

export const HubCustomFieldModal: React.FC<HubCustomFieldModalProps> = observer(({ vm }) => {
    const draft = vm.getEditingCustomFieldDraft();

    if (!draft) {
        return null;
    }

    const isVisible = vm.isEditingCustomFieldVisible();
    const isMandatory = vm.isEditingCustomFieldMandatory();
    const isLocked = draft.locked || (draft.customFieldId > 0);
    const isNewField = vm.isCreatingNewCustomField();
    const hasRichTextLabel = vm.isRichTextLabelType(draft.type);
    const labelError = vm.getEditingCustomFieldLabelError();

    const handleAddClick = (): void => {
        vm.applyCustomFieldEditor();
    };

    const handleLabelEditorChange = (_: unknown, editor: Editor): void => {
        const label = editor.getData();
        vm.setEditingCustomFieldLabel(label);
    };

    const isConfirmingDelete = vm.isShowingDeleteConfirmation();
    const fieldToDeleteId = vm.getFieldToDelete()?.fieldId;

    return (
        <>
            <CommonModal
                isOpen
                onRequestClose={vm.closeCustomFieldEditor}
                overlayClassName='hub-custom-field-editor-overlay'
                className='hub-custom-field-editor__modal'
                classNameBody='hub-custom-field-editor__body-wrapper'
                title={
                    <FormattedMessage
                        id='oe.hub.customField.modalTitle'
                        description='Add/Edit custom field title'
                        defaultMessage={isNewField ? 'Add Custom Field' : 'Edit Custom Field'}
                    />
                }
                footer={
                    <div className='hub-custom-field-editor__footer'>
                        <DefaultButton
                            modifier='cancel'
                            text={<FormattedMessage {...CANCEL_TEXT} />}
                            onClick={vm.closeCustomFieldEditor}
                        />
                        <DefaultButton
                            text={<FormattedMessage {...ADD_TEXT} />}
                            onClick={handleAddClick}
                        />
                    </div>
                }
            >
            <div className='hub-custom-field-editor__body'>
                <div className='hub-custom-field-editor__row'>
                    <FormField
                        label={
                            <FormattedMessage
                                id='oe.label.fieldType'
                                description='Field type label'
                                defaultMessage='Field Type'
                            />
                        }
                        className='hub-custom-field-editor__field hub-custom-field-editor__field--half'
                    >
                        <select
                            className='oe-form-field__control'
                            value={draft.type}
                            disabled={isLocked}
                            onChange={event => vm.setEditingCustomFieldType(event.target.value as HubCustomFieldType)}
                        >
                            <option value={HubCustomFieldType.Text}>
                                {vm.intl.formatMessage(HUB_CUSTOM_FIELD_TYPE_TEXT_TEXT)}
                            </option>
                            <option value={HubCustomFieldType.Dropdown}>
                                {vm.intl.formatMessage(HUB_CUSTOM_FIELD_TYPE_DROPDOWN_TEXT)}
                            </option>
                            <option value={HubCustomFieldType.Textarea}>
                                {vm.intl.formatMessage(HUB_CUSTOM_FIELD_TYPE_TEXTAREA_TEXT)}
                            </option>
                            <option value={HubCustomFieldType.Checkbox}>
                                {vm.intl.formatMessage(HUB_CUSTOM_FIELD_TYPE_CHECKBOX_TEXT)}
                            </option>
                        </select>
                        {vm.getEditingCustomFieldTypeError() && (
                            <span className='oe-form-field__error'>{vm.getEditingCustomFieldTypeError()}</span>
                        )}
                    </FormField>

                    <FormField
                        label={
                            <FormattedMessage
                                id='oe.label.fieldId'
                                description='Field ID label'
                                defaultMessage='Field ID'
                            />
                        }
                        className='hub-custom-field-editor__field hub-custom-field-editor__field--half'
                    >
                        <input
                            className='oe-form-field__control'
                            value={draft.fieldId}
                            type='text'
                            disabled={isLocked}
                            onChange={event => vm.setEditingCustomFieldId(event.target.value)}
                        />
                        {vm.getEditingCustomFieldIdError() && (
                            <span className='oe-form-field__error'>{vm.getEditingCustomFieldIdError()}</span>
                        )}
                    </FormField>
                </div>

                {draft.type === HubCustomFieldType.Dropdown && (
                    <FormField
                        label={
                            <FormattedMessage
                                id='oe.label.fieldOptions'
                                description='Field options label'
                                defaultMessage='Options'
                            />
                        }
                        className='hub-custom-field-editor__field'
                    >
                        <textarea
                            className='oe-form-field__control hub-custom-field-editor__description'
                            value={vm.getEditingCustomFieldOptionsText()}
                            onChange={event => vm.setEditingCustomFieldOptions(event.target.value)}
                        />
                        <p className='hub-custom-field-editor__field-hint'>
                            <FormattedMessage
                                id='oe.hub.customField.optionsHint'
                                description='Field options hint text'
                                defaultMessage='Enter each option on a new line. At least one option is required.'
                            />
                        </p>
                        {vm.getEditingCustomFieldOptionsError() && (
                            <span className='oe-form-field__error'>{vm.getEditingCustomFieldOptionsError()}</span>
                        )}
                    </FormField>
                )}

                <FormField
                    label={
                        <FormattedMessage
                            id='oe.label.fieldLabel'
                            description='Field label label'
                            defaultMessage='Field Label'
                        />
                    }
                    className='hub-custom-field-editor__field'
                >
                    {hasRichTextLabel ? (
                        <RichTextEditor
                            className='oe-form-field__control m-0px p-0px'
                            value={draft.label}
                            onChange={handleLabelEditorChange}
                            validationError={labelError || undefined}
                            dataTestId='hub-custom-field-label-editor'
                        />
                    ) : (
                        <>
                            <input
                                className='oe-form-field__control'
                                value={draft.label}
                                type='text'
                                onChange={event => vm.setEditingCustomFieldLabel(event.target.value)}
                            />
                            {labelError && (
                                <span className='oe-form-field__error'>{labelError}</span>
                            )}
                        </>
                    )}
                </FormField>

                <FormField
                    label={
                        <FormattedMessage
                            id='oe.label.fieldDescription'
                            description='Field description label'
                            defaultMessage='Description'
                        />
                    }
                    className='hub-custom-field-editor__field'
                >
                    <textarea
                        className='oe-form-field__control hub-custom-field-editor__description'
                        value={draft.description || ''}
                        onChange={event => vm.setEditingCustomFieldDescription(event.target.value)}
                    />
                    {vm.getEditingCustomFieldDescriptionError() && (
                        <span className='oe-form-field__error'>{vm.getEditingCustomFieldDescriptionError()}</span>
                    )}
                </FormField>

                <div className='hub-custom-field-editor__toggles'>
                    <div className='hub-custom-field-editor__toggle-row'>
                        <OEToggle
                            isSelected={isVisible}
                            onClick={vm.toggleEditingCustomFieldVisibility}
                        />
                        <FormattedMessage {...HUB_REGISTRATION_FIELD_DISPLAYED_TOGGLE_TEXT} />
                    </div>

                    <div className='hub-custom-field-editor__toggle-row'>
                        <OEToggle
                            isSelected={isMandatory}
                            onClick={vm.toggleEditingCustomFieldMandatory}
                            isDisabled={!isVisible}
                        />
                        <FormattedMessage {...HUB_REGISTRATION_FIELD_REQUIRED_TOGGLE_TEXT} />
                    </div>
                </div>

                {!isNewField && (
                    <button
                        className='hub-custom-field-editor__delete-field-button'
                        onClick={() => vm.deleteCustomField(draft)}
                        type='button'
                    >
                        <FormattedMessage
                            id='oe.hub.deleteCustomField'
                            description='Delete Custom Field button'
                            defaultMessage='Delete Custom Field'
                        />
                    </button>
                )}
            </div>
            </CommonModal>

            {isConfirmingDelete && (
                <CommonModal
                    isOpen
                    onRequestClose={vm.cancelDeleteCustomField}
                    keepPreviousModalVisible
                    supressRenderingCrossIcon
                    overlayClassName='hub-custom-field-delete-confirm-overlay'
                    className='hub-custom-field-delete-confirm__modal'
                    footer={
                        <div className='hub-custom-field-delete-confirm__footer'>
                            <DefaultButton
                                modifier='cancel'
                                text={<FormattedMessage {...CANCEL_TEXT} />}
                                onClick={vm.cancelDeleteCustomField}
                            />
                            <DefaultButton
                                modifier='confirm-warn'
                                text={
                                    <FormattedMessage
                                        id='oe.hub.yesDelete'
                                        description='Confirm delete button'
                                        defaultMessage='Yes, Delete'
                                    />
                                }
                                onClick={vm.confirmDeleteCustomField}
                            />
                        </div>
                    }
                >
                    <div className='hub-custom-field-delete-confirm__body'>
                        <FormattedMessage
                            id='oe.hub.deleteCustomFieldConfirmMessage'
                            description='Delete custom field confirmation message'
                            defaultMessage='Are you sure you want to <highlight>delete {fieldId}</highlight>? Any data collected in this field will be deleted.'
                            values={{
                                fieldId: fieldToDeleteId,
                                highlight: (chunks: React.ReactNode) => (
                                    <span className='hub-custom-field-delete-confirm__highlight'>{chunks}</span>
                                ),
                            }}
                        />
                    </div>
                </CommonModal>
            )}
        </>
    );
});
